import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as FileUploadIcon } from '../../assets/icons/file-input.svg';
import { MultiMediaData } from '../../interface/charging-location.interface';
import ModalBox from '../modal-box/ModalBox';
import { TooltipIcon } from '../tooltip/TooltipIcon';

import GalleryView from './components/GalleryView';

interface GalleryViewFileProperties {
  url: string;
  signedUrl: string;
  fileName: string;
  fileType: string;
  originalIndex: number;
  type: 'newFile' | 'existingFile';
}

interface CustomMediaUploadProperties {
  inputLabel?: string;
  acceptedFileType: string;
  placeholder: string;
  selectedFiles: File[] | null;
  name: string;
  onChangeValue: (name: string, file: File[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  unselectHandler?: (
    name: string,
    originalIndex: number,
    url: string,
    type: 'newFile' | 'existingFile',
  ) => void;
  existingName?: string;
  existingFiles?: MultiMediaData[] | null;
  error?: boolean;
  errorMessage?: string;
  tooltipText?: string;
  maxImageSizeMB?: number;
  maxVideoSizeMB?: number;
  maxFileCount?: number;
}

const DEFAULT_MAX_IMAGE_SIZE_MB = 1;
const DEFAULT_MAX_VIDEO_SIZE_MB = 100;

/**
 * Custom media upload with View
 * @param {any} param0 - The param0
 * @param {any} param0.inputLabel - The inputLabel prop
 * @param {any} param0.acceptedFileType - The acceptedFileType prop
 * @param {any} param0.name - The name prop
 * @param {any} param0.placeholder - The placeholder prop
 * @param {any} param0.selectedFiles - The selectedFiles prop
 * @param {any} param0.onChangeValue - The onChangeValue prop
 * @param {any} param0.valueRequired - The valueRequired prop
 * @param {any} param0.readOnly - The readOnly prop
 * @param {any} param0.unselectHandler - The unselectHandler prop
 * @param {any} param0.existingName - The existingName prop
 * @param {any} param0.existingFiles - The existingFiles prop
 * @param {any} param0.error - The error prop
 * @param {any} param0.errorMessage - The errorMessage prop
 * @param {any} param0.tooltipText - The tooltipText prop
 * @param {any} param0.maxImageSizeMB - Max allowed size in MB for image files; oversized files are rejected
 * @param {any} param0.maxVideoSizeMB - Max allowed size in MB for video files; oversized files are rejected
 * @param {any} param0.maxFileCount - Max total number of files (existing + newly selected) allowed; extra files are rejected
 */
function CustomMediaUpload({
  inputLabel,
  acceptedFileType,
  name,
  placeholder,
  selectedFiles,
  onChangeValue,
  valueRequired,
  readOnly,
  unselectHandler,
  existingName,
  existingFiles,
  error,
  errorMessage,
  tooltipText,
  maxImageSizeMB,
  maxVideoSizeMB,
  maxFileCount,
}: CustomMediaUploadProperties) {
  const { t } = useTranslation('sharedComponent');
  const inputReference = useRef<HTMLInputElement | null>(null);
  const [galleryView, setGalleryView] = useState<'open' | 'close'>('close');
  const [galleryViewData, setGalleryViewData] = useState<GalleryViewFileProperties[]>([]);
  const [sizeErrorMessage, setSizeErrorMessage] = useState<string>('');

  /**
   * handle add new file, rejecting files that exceed the type-based size limit
   * @param {File[]} selectedFileList - Files chosen from the file input
   */
  const handleSelectFile = (selectedFileList: Array<File>) => {
    if (!selectedFileList?.length) return;

    const imageLimitBytes = (maxImageSizeMB ?? DEFAULT_MAX_IMAGE_SIZE_MB) * 1024 * 1024;
    const videoLimitBytes = (maxVideoSizeMB ?? DEFAULT_MAX_VIDEO_SIZE_MB) * 1024 * 1024;

    const acceptedFiles: File[] = [];
    const oversizedImageFiles: File[] = [];
    const oversizedVideoFiles: File[] = [];

    selectedFileList.forEach((file) => {
      const isVideo = file.type.includes('video');
      const limitBytes = isVideo ? videoLimitBytes : imageLimitBytes;
      if (file.size > limitBytes) {
        (isVideo ? oversizedVideoFiles : oversizedImageFiles).push(file);
      } else {
        acceptedFiles.push(file);
      }
    });

    let countExceededMessage = '';
    let filesWithinCountLimit = acceptedFiles;
    if (maxFileCount) {
      const currentFileCount = (selectedFiles?.length || 0) + (existingFiles?.length || 0);
      const remainingSlots = Math.max(maxFileCount - currentFileCount, 0);
      if (acceptedFiles.length > remainingSlots) {
        filesWithinCountLimit = acceptedFiles.slice(0, remainingSlots);
        countExceededMessage = t('maxFilesAllowed', { maxFileCount });
      }
    }

    /**
     * Build a size-limit-exceeded message for a group of same-type files
     * @param {File[]} files - Oversized files of a single type
     * @param {number} limitMB - Size limit in MB that applies to this type
     * @returns {string} Human-readable rejection message, or empty string if no files
     */
    const buildSizeErrorMessage = (files: File[], limitMB: number) => {
      if (!files.length) return '';
      return files.length === 1
        ? t('sizeLimitExceededSingle', { limitMB })
        : t('sizeLimitExceededMultiple', { limitMB });
    };

    const messages = [
      buildSizeErrorMessage(oversizedImageFiles, maxImageSizeMB ?? DEFAULT_MAX_IMAGE_SIZE_MB),
      buildSizeErrorMessage(oversizedVideoFiles, maxVideoSizeMB ?? DEFAULT_MAX_VIDEO_SIZE_MB),
      countExceededMessage,
    ].filter(Boolean);

    setSizeErrorMessage(messages.join(' '));

    if (filesWithinCountLimit.length) {
      onChangeValue(name, [...(selectedFiles || []), ...filesWithinCountLimit]);
    }
  };

  /**
   * Handle remove file
   * @param {any} originalIndex - The originalIndex
   * @param {any} url - The url
   * @param {any} type - The type
   */
  const handleRemoveSelectedFile = (
    originalIndex: number,
    url: string,
    type: 'newFile' | 'existingFile',
  ) => {
    if (unselectHandler && type === 'newFile') {
      unselectHandler(name, originalIndex, url, type);
    } else if (unselectHandler && type === 'existingFile' && existingName) {
      unselectHandler(existingName, originalIndex, url, type);
    } else {
      const afterRemovedSelectedFile =
        selectedFiles?.filter((_, index) => index !== originalIndex) || [];
      onChangeValue(name, afterRemovedSelectedFile);
    }
  };

  useEffect(() => {
    let filesForGalleryView: GalleryViewFileProperties[] = [];
    if (selectedFiles) {
      const selectedFilesForGallery: GalleryViewFileProperties[] = selectedFiles?.map(
        (eachFile, index) => ({
          url: URL.createObjectURL(eachFile),
          signedUrl: URL.createObjectURL(eachFile),
          fileName: eachFile.name,
          fileType: eachFile.type,
          originalIndex: index,
          type: 'newFile',
        }),
      );
      filesForGalleryView = selectedFilesForGallery;
    }
    if (existingFiles) {
      const existingFilesForGallery: GalleryViewFileProperties[] = existingFiles?.map(
        (eachFile, index) => ({
          url: eachFile.url,
          signedUrl: eachFile.signedUrl,
          fileName: eachFile.fileName,
          fileType: eachFile.fileType,
          originalIndex: index,
          type: 'existingFile',
        }),
      );
      filesForGalleryView = [...filesForGalleryView, ...existingFilesForGallery];
    }

    setGalleryViewData(filesForGalleryView);
  }, [selectedFiles, existingFiles]);

  return (
    <div className="custom-file-input-wrap">
      <ModalBox
        open={galleryView === 'open'}
        title={t('media')}
        content={
          <GalleryView
            selectedFiles={galleryViewData}
            handleRemoveSelectedFile={handleRemoveSelectedFile}
            acceptedFileType={acceptedFileType}
            handleSelectFile={handleSelectFile}
            readOnly={readOnly}
            onSubmit={() => setGalleryView('close')}
          />
        }
        closeModal={() => setGalleryView('close')}
        header
        rightCorner=""
      />
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
          {tooltipText && <TooltipIcon text={tooltipText} />}
        </p>
      )}
      <div className={`custom-file-input-container ${error && 'error'}`}>
        <div className="selected-file-wrap">
          {(() => {
            let selectedFilesView;
            if (galleryViewData?.length === 0) {
              selectedFilesView = <p className="placeholder">{placeholder}</p>;
            } else if (galleryViewData?.length <= 4) {
              selectedFilesView = (
                <div className="selected-files-view-wrap">
                  {galleryViewData?.map((selectFile) => (
                    <div className="single-selected-wrap">
                      {(() => {
                        let fileTypeView;
                        if (selectFile?.fileType?.includes('image')) {
                          fileTypeView = (
                            <img src={selectFile.signedUrl} alt="" className="selected-image" />
                          );
                        } else if (selectFile?.fileType?.includes('video')) {
                          fileTypeView = (
                            <video controls={false} className="selected-video">
                              <source src={selectFile.signedUrl} type="video/mp4" />
                              <track
                                src="path/to/captions.vtt"
                                kind="captions"
                                label="English captions"
                                default
                              />
                              Your browser does not support the video tag.
                            </video>
                          );
                        }
                        return fileTypeView;
                      })()}
                    </div>
                  ))}
                </div>
              );
            } else if (galleryViewData?.length > 4) {
              selectedFilesView = (
                <div className="selected-files-view-wrap">
                  {galleryViewData.slice(0, 3).map((selectFile) => (
                    <div className="single-selected-wrap">
                      {(() => {
                        let fileTypeView;
                        if (selectFile?.fileType?.includes('image')) {
                          fileTypeView = (
                            <img src={selectFile.signedUrl} alt="" className="selected-image" />
                          );
                        } else if (selectFile?.fileType?.includes('video')) {
                          fileTypeView = (
                            <video controls={false} className="selected-video">
                              <source src={selectFile.signedUrl} type="video/mp4" />
                              <track
                                src="path/to/captions.vtt"
                                kind="captions"
                                label={t('englishCaptions')}
                                default
                              />
                              {t('videoNotSupported')}
                            </video>
                          );
                        }
                        return fileTypeView;
                      })()}
                    </div>
                  ))}
                  <div className="single-selected-wrap">
                    <p
                      className="number-of-files"
                      role="presentation"
                      onClick={() => setGalleryView('open')}>
                      +{galleryViewData.length - 3}
                    </p>
                  </div>
                </div>
              );
            }
            return selectedFilesView;
          })()}
        </div>
        <input
          ref={inputReference}
          type="file"
          accept={acceptedFileType}
          multiple
          style={{ display: 'none' } as React.CSSProperties}
          onChange={(event) => {
            const files = event?.target?.files;
            if (files) {
              handleSelectFile(Array.from(files));
            }
            if (inputReference.current) {
              inputReference.current.value = '';
            }
          }}
        />
        <div className="file-input-icon-wrap">
          {galleryViewData?.length === 0 ? (
            <span
              className={`file-input-icon ${readOnly ? 'disabled' : ''} `}
              role="presentation"
              onClick={() => {
                if (!readOnly) {
                  inputReference.current?.click();
                }
              }}>
              <FileUploadIcon />
            </span>
          ) : (
            <span
              className="file-input-icon"
              role="presentation"
              onClick={() => setGalleryView('open')}>
              <EyeIcon />
            </span>
          )}
        </div>
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
      {sizeErrorMessage && <p className="custom-input-error-message">{sizeErrorMessage}</p>}
    </div>
  );
}

CustomMediaUpload.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  unselectHandler: null,
  existingName: '',
  existingFiles: [],
  error: false,
  errorMessage: '',
  tooltipText: '',
  maxImageSizeMB: DEFAULT_MAX_IMAGE_SIZE_MB,
  maxVideoSizeMB: DEFAULT_MAX_VIDEO_SIZE_MB,
  maxFileCount: null,
};

export default CustomMediaUpload;
